import { Card } from '@/components/common/card'
import { Markdown } from '@/components/common/markdown'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/tabs'
import { StrategyOverview } from '@/components/opportunity/opportunityOverview/strategyOverview'
import { StrategyPerformance } from '@/components/opportunity/opportunityOverview/strategyPerformance'
import { VaultFlags } from '@/components/opportunity/opportunityOverview/vaultFlags'
import { PositionDetails } from '@/components/opportunity/positionDetails/positionDetails'
import { OPPORTUNITY_PAGE_KEYS } from '@/constants/state'
import { useSearchState } from '@/hooks/useSearchState'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { Vault } from '@/types/vault'
import type { Address } from 'viem'
import { CurrentAllocations } from './sections/currentAllocations'
import { OwnersDistribution } from './sections/ownersDistribution'
import { ReputationScore } from './sections/reputationScore'

export const OPPORTUNITY_TAB_VALUES = {
  overview: 'overview',
  position: 'position',
} as const

const TABS = [
  { key: OPPORTUNITY_TAB_VALUES.overview, label: 'Overview' },
  { key: OPPORTUNITY_TAB_VALUES.position, label: 'Position details' },
] as const

type TabKey = (typeof TABS)[number]['key']

interface OpportunityContentProps {
  address: Address | undefined
  vault: Vault
  transactionsContext?: TransactionsContext
}

export const OpportunityContent = ({ address, vault, transactionsContext }: OpportunityContentProps) => {
  const { score, description, flags } = vault
  const { searchState, updateSearchState } = useSearchState({})

  const rawTab = searchState.get(OPPORTUNITY_PAGE_KEYS.tab.key)
  const isPositionDisabled = !address
  const tabFromUrl = TABS.some((t) => t.key === rawTab) ? (rawTab as TabKey) : OPPORTUNITY_TAB_VALUES.overview
  const activeTab: TabKey =
    tabFromUrl === OPPORTUNITY_TAB_VALUES.position && isPositionDisabled ? OPPORTUNITY_TAB_VALUES.overview : tabFromUrl

  return (
    <>
      <VaultFlags flags={flags} />
      <Tabs
        value={activeTab}
        onValueChange={(value) => updateSearchState([OPPORTUNITY_PAGE_KEYS.tab.key, value])}
        className="overflow-hidden"
      >
        <div className="flex flex-col gap-4">
          <TabsList variant="line">
            <TabsTrigger variant="line" value={OPPORTUNITY_TAB_VALUES.overview}>
              Overview
            </TabsTrigger>
            <TabsTrigger variant="line" value={OPPORTUNITY_TAB_VALUES.position} disabled={isPositionDisabled}>
              Your position & activity
            </TabsTrigger>
          </TabsList>
          <TabsContent value={OPPORTUNITY_TAB_VALUES.overview}>
            <div className="flex flex-col gap-4">
              <StrategyPerformance vault={vault} />
              <Card className="px-4 py-3 md:px-5">
                <StrategyOverview vault={vault} />
              </Card>
              {description && (
                <Card className="flex flex-col gap-2 p-4 md:p-5">
                  <span className="font-medium text-base text-heading-foreground">Where does the yield come from?</span>
                  <Markdown className="text-body-foreground text-sm">{description}</Markdown>
                </Card>
              )}
              <div className="flex w-full flex-wrap-reverse gap-4">
                <OwnersDistribution vault={vault} className="flex-grow p-4 md:p-5" />
                {score && <ReputationScore vault={vault} className="flex-grow p-4 md:p-5" />}
              </div>
              <CurrentAllocations vault={vault} />
            </div>
          </TabsContent>
          <TabsContent value={OPPORTUNITY_TAB_VALUES.position}>
            <PositionDetails address={address} vault={vault} transactionsContext={transactionsContext} />
          </TabsContent>
        </div>
      </Tabs>
    </>
  )
}
